import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * PDFPageSidebar - Collapsible sidebar showing PDF page thumbnails
 * With page management features: delete, rotate, extract, add blank
 * On desktop: left sidebar
 * On mobile: bottom sheet modal
 */
export const PDFPageSidebar = ({
  pdfLayer,
  onChangePage,
  onRelinkPdf,
  onDeletePages,
  onRotatePage,
  onAddBlankPage,
  onExtractPages,
  onClose,
  isMobile = false,
}) => {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [isRelinking, setIsRelinking] = useState(false);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [showManageMode, setShowManageMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: null, data: null });
  const fileInputRef = useRef(null);

  const { pdfDoc, currentPage = 1, pageCount = 1, pageScales = {}, pageRotations = {} } = pdfLayer || {};

  // Handle re-linking PDF file
  const handleRelinkClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.includes('pdf')) return;

    setIsRelinking(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdfDoc.numPages;

      if (onRelinkPdf) {
        onRelinkPdf(pdfDoc, numPages, arrayBuffer);
      }
    } catch (err) {
      console.error('Error re-linking PDF:', err);
      alert('Failed to load PDF. Please try again.');
    } finally {
      setIsRelinking(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Generate thumbnails for all pages
  useEffect(() => {
    const generateThumbnails = async () => {
      if (!pdfDoc || pageCount <= 1) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const thumbs = [];
      const thumbScale = 0.3;

      try {
        for (let i = 1; i <= pageCount; i++) {
          const page = await pdfDoc.getPage(i);
          // Apply rotation if set
          const rotation = pageRotations[i] || 0;
          const viewport = page.getViewport({ scale: thumbScale, rotation });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          thumbs.push({
            pageNum: i,
            dataUrl: canvas.toDataURL('image/jpeg', 0.6),
            width: viewport.width,
            height: viewport.height,
            rotation,
          });
        }
        setThumbnails(thumbs);
      } catch (err) {
        console.error('Error generating thumbnails:', err);
      } finally {
        setLoading(false);
      }
    };

    generateThumbnails();
  }, [pdfDoc, pageCount, pageRotations]);

  const handlePageClick = useCallback(async (pageNum) => {
    if (showManageMode) {
      // In manage mode, toggle selection
      setSelectedPages(prev => {
        const newSet = new Set(prev);
        if (newSet.has(pageNum)) {
          newSet.delete(pageNum);
        } else {
          newSet.add(pageNum);
        }
        return newSet;
      });
      return;
    }

    if (pageNum === currentPage || isChangingPage) return;
    if (!pdfDoc) return;

    setIsChangingPage(true);
    await onChangePage(pageNum);
    setIsChangingPage(false);

    if (isMobile) {
      onClose();
    }
  }, [currentPage, isChangingPage, onChangePage, isMobile, onClose, pdfDoc, showManageMode]);

  // Page management actions
  const handleDeleteSelected = useCallback(() => {
    if (selectedPages.size === 0) return;
    if (selectedPages.size === pageCount) {
      alert('Cannot delete all pages. At least one page must remain.');
      return;
    }

    const pageList = Array.from(selectedPages).sort((a, b) => a - b).join(', ');
    setConfirmDialog({
      isOpen: true,
      type: 'delete',
      data: { pages: Array.from(selectedPages) },
      title: 'Delete Pages',
      message: `Are you sure you want to delete page${selectedPages.size > 1 ? 's' : ''} ${pageList}? This action cannot be undone.`,
    });
  }, [selectedPages, pageCount]);

  const handleRotateSelected = useCallback((direction) => {
    if (selectedPages.size === 0) return;

    const rotation = direction === 'cw' ? 90 : -90;
    selectedPages.forEach(pageNum => {
      onRotatePage?.(pageNum, rotation);
    });
  }, [selectedPages, onRotatePage]);

  const handleExtractSelected = useCallback(() => {
    if (selectedPages.size === 0) return;

    setConfirmDialog({
      isOpen: true,
      type: 'extract',
      data: { pages: Array.from(selectedPages) },
      title: 'Extract Pages',
      message: `Extract ${selectedPages.size} page${selectedPages.size > 1 ? 's' : ''} to a new project? The selected pages will be copied (not removed from current project).`,
    });
  }, [selectedPages]);

  const handleAddBlankPage = useCallback(() => {
    setConfirmDialog({
      isOpen: true,
      type: 'addBlank',
      data: { afterPage: currentPage },
      title: 'Add Blank Page',
      message: `Add a blank page after page ${currentPage} for sketching?`,
    });
  }, [currentPage]);

  const handleConfirmAction = useCallback(() => {
    const { type, data } = confirmDialog;

    switch (type) {
      case 'delete':
        onDeletePages?.(data.pages);
        setSelectedPages(new Set());
        break;
      case 'extract':
        onExtractPages?.(data.pages);
        break;
      case 'addBlank':
        onAddBlankPage?.(data.afterPage);
        break;
    }

    setConfirmDialog({ isOpen: false, type: null, data: null });
  }, [confirmDialog, onDeletePages, onExtractPages, onAddBlankPage]);

  const handleSelectAll = useCallback(() => {
    if (selectedPages.size === pageCount) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set(Array.from({ length: pageCount }, (_, i) => i + 1)));
    }
  }, [selectedPages, pageCount]);

  const exitManageMode = useCallback(() => {
    setShowManageMode(false);
    setSelectedPages(new Set());
  }, []);

  // Don't render if single page and not in manage mode
  if (pageCount <= 1 && !showManageMode) return null;

  const isPdfDocMissing = !pdfDoc;

  // Management toolbar
  const ManagementToolbar = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '8px',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      background: 'rgba(0,0,0,0.2)',
    }}>
      {showManageMode ? (
        <>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: '#00c8ff', fontSize: '11px' }}>
              {selectedPages.size} selected
            </span>
            <Button
              variant="ghost"
              size="small"
              onClick={exitManageMode}
              style={{
                fontSize: '11px',
                textDecoration: 'underline',
                padding: '4px 8px',
              }}
            >
              Done
            </Button>
          </div>

          <div style={{
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
          }}>
            <ActionButton
              icon="☑"
              label={selectedPages.size === pageCount ? 'None' : 'All'}
              onClick={handleSelectAll}
              disabled={isPdfDocMissing}
            />
            <ActionButton
              icon="↻"
              label="CW"
              onClick={() => handleRotateSelected('cw')}
              disabled={selectedPages.size === 0}
              title="Rotate clockwise 90deg"
            />
            <ActionButton
              icon="↺"
              label="CCW"
              onClick={() => handleRotateSelected('ccw')}
              disabled={selectedPages.size === 0}
              title="Rotate counter-clockwise 90deg"
            />
            <ActionButton
              icon="📤"
              label="Extract"
              onClick={handleExtractSelected}
              disabled={selectedPages.size === 0}
              title="Extract to new project"
            />
            <ActionButton
              icon="🗑"
              label="Delete"
              onClick={handleDeleteSelected}
              disabled={selectedPages.size === 0 || selectedPages.size === pageCount}
              danger
              title="Delete selected pages"
            />
          </div>
        </>
      ) : (
        <div style={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'center',
        }}>
          <ActionButton
            icon="✏️"
            label="Manage"
            onClick={() => setShowManageMode(true)}
            disabled={isPdfDocMissing}
          />
          <ActionButton
            icon="➕"
            label="Blank"
            onClick={handleAddBlankPage}
            disabled={isPdfDocMissing}
            title="Add blank page"
          />
        </div>
      )}
    </div>
  );

  // Mobile: Bottom sheet modal
  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 999,
          }}
        />

        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '70vh',
            background: 'linear-gradient(180deg, rgba(20,28,36,0.98) 0%, rgba(12,18,24,0.99) 100%)',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <span style={{ color: '#00c8ff', fontSize: '14px', fontWeight: '600' }}>
              PDF Pages ({pageCount})
            </span>
            <Button
              variant="ghost"
              size="small"
              onClick={onClose}
              style={{
                fontSize: '20px',
                padding: '4px 8px',
              }}
            >
              ×
            </Button>
          </div>

          {!isPdfDocMissing && <ManagementToolbar />}

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px',
            }}
          >
            {isPdfDocMissing ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <div style={{ color: '#ff8866', marginBottom: '8px' }}>PDF document not available</div>
                <div style={{ fontSize: '12px', color: '#6080a0', marginBottom: '12px' }}>
                  Re-link the same PDF to enable page navigation
                </div>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleRelinkClick}
                  disabled={isRelinking}
                  style={{ fontSize: '12px' }}
                >
                  {isRelinking ? 'Loading...' : 'Re-link PDF'}
                </Button>
              </div>
            ) : loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6080a0' }}>
                Loading pages...
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px',
                }}
              >
                {thumbnails.map((thumb) => (
                  <ThumbnailItem
                    key={thumb.pageNum}
                    thumb={thumb}
                    isActive={thumb.pageNum === currentPage}
                    isSelected={selectedPages.has(thumb.pageNum)}
                    isCalibrated={!!pageScales[thumb.pageNum]}
                    isChanging={isChangingPage}
                    isManageMode={showManageMode}
                    onClick={() => handlePageClick(thumb.pageNum)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          variant={confirmDialog.type === 'delete' ? 'danger' : 'info'}
          confirmText={confirmDialog.type === 'delete' ? 'Delete' : 'Confirm'}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmDialog({ isOpen: false, type: null, data: null })}
        />
      </>
    );
  }

  // Desktop: Left sidebar
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '160px',
        height: '100%',
        background: 'linear-gradient(90deg, rgba(12,18,24,0.98) 0%, rgba(15,22,30,0.95) 100%)',
        borderRight: '1px solid rgba(0,200,255,0.2)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
        boxShadow: '4px 0 12px rgba(0,0,0,0.3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.2)',
        }}
      >
        <span style={{ color: '#00c8ff', fontSize: '11px', fontWeight: '600' }}>
          Pages ({pageCount})
        </span>
        <Button
          variant="ghost"
          size="small"
          onClick={onClose}
          title="Close sidebar"
          style={{
            fontSize: '16px',
            padding: '2px 6px',
            lineHeight: 1,
          }}
        >
          ×
        </Button>
      </div>

      {!isPdfDocMissing && <ManagementToolbar />}

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
        }}
      >
        {isPdfDocMissing ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div style={{ color: '#ff8866', fontSize: '11px', marginBottom: '6px' }}>PDF not available</div>
            <div style={{ fontSize: '10px', color: '#6080a0', marginBottom: '10px' }}>
              Re-link the same PDF to navigate pages
            </div>
            <Button
              variant="primary"
              size="small"
              onClick={handleRelinkClick}
              disabled={isRelinking}
              style={{ fontSize: '10px' }}
            >
              {isRelinking ? 'Loading...' : 'Re-link PDF'}
            </Button>
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6080a0', fontSize: '11px' }}>
            Loading...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {thumbnails.map((thumb) => (
              <ThumbnailItem
                key={thumb.pageNum}
                thumb={thumb}
                isActive={thumb.pageNum === currentPage}
                isSelected={selectedPages.has(thumb.pageNum)}
                isCalibrated={!!pageScales[thumb.pageNum]}
                isChanging={isChangingPage}
                isManageMode={showManageMode}
                onClick={() => handlePageClick(thumb.pageNum)}
                compact
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.type === 'delete' ? 'danger' : 'info'}
        confirmText={confirmDialog.type === 'delete' ? 'Delete' : 'Confirm'}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ isOpen: false, type: null, data: null })}
      />
    </div>
  );
};

/**
 * Small action button for page management
 */
const ActionButton = ({ icon, label, onClick, disabled, danger, title }) => (
  <Button
    variant={danger ? 'danger' : 'default'}
    size="small"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      flexDirection: 'column',
      gap: '2px',
      padding: '6px 8px',
      minWidth: '40px',
      fontSize: '10px',
    }}
  >
    <span style={{ fontSize: '12px' }}>{icon}</span>
    <span>{label}</span>
  </Button>
);

/**
 * Individual thumbnail item
 */
const ThumbnailItem = ({ thumb, isActive, isSelected, isCalibrated, isChanging, isManageMode, onClick, compact = false }) => {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: isChanging ? 'wait' : 'pointer',
        opacity: isChanging ? 0.6 : 1,
        transition: 'all 0.15s ease',
        position: 'relative',
      }}
    >
      {/* Selection checkbox overlay */}
      {isManageMode && (
        <div
          style={{
            position: 'absolute',
            top: compact ? '4px' : '6px',
            left: compact ? '4px' : '6px',
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            background: isSelected ? '#00c8ff' : 'rgba(0,0,0,0.6)',
            border: `2px solid ${isSelected ? '#00c8ff' : 'rgba(255,255,255,0.4)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          {isSelected && (
            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
          )}
        </div>
      )}

      {/* Rotation indicator */}
      {thumb.rotation !== 0 && (
        <div
          style={{
            position: 'absolute',
            top: compact ? '4px' : '6px',
            right: compact ? '4px' : '6px',
            background: 'rgba(255,170,0,0.9)',
            color: '#000',
            fontSize: '8px',
            padding: '2px 4px',
            borderRadius: '3px',
            fontWeight: 'bold',
            zIndex: 10,
          }}
        >
          {thumb.rotation}°
        </div>
      )}

      <div
        style={{
          border: isSelected
            ? '3px solid #00ffaa'
            : isActive
            ? '3px solid #00c8ff'
            : '2px solid rgba(255,255,255,0.15)',
          borderRadius: compact ? '4px' : '6px',
          padding: compact ? '2px' : '3px',
          background: isSelected
            ? 'rgba(0,255,170,0.15)'
            : isActive
            ? 'rgba(0,200,255,0.15)'
            : 'rgba(255,255,255,0.03)',
          transition: 'all 0.15s ease',
        }}
      >
        <img
          src={thumb.dataUrl}
          alt={`Page ${thumb.pageNum}`}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: compact ? '2px' : '4px',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          marginTop: compact ? '3px' : '4px',
        }}
      >
        <span
          style={{
            fontSize: compact ? '10px' : '11px',
            color: isSelected ? '#00ffaa' : isActive ? '#00c8ff' : '#6080a0',
            fontWeight: isActive || isSelected ? '600' : '400',
          }}
        >
          {thumb.pageNum}
        </span>
        {isCalibrated && (
          <span
            style={{
              fontSize: '8px',
              color: '#00ffaa',
            }}
            title="Scale calibrated"
          >
            ✓
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Toggle button for showing/hiding the PDF page sidebar
 */
export const PDFPageSidebarToggle = ({ onClick, isOpen, pageCount, currentPage, isMobile = false }) => {
  if (pageCount <= 1) return null;

  if (isMobile) {
    return (
      <Button
        variant="default"
        active={isOpen}
        onClick={onClick}
        style={{
          position: 'fixed',
          bottom: 70,
          left: 10,
          padding: '8px 12px',
          fontSize: '12px',
          fontWeight: '500',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 50,
        }}
      >
        <span style={{ fontSize: '14px' }}>📄</span>
        {currentPage}/{pageCount}
      </Button>
    );
  }

  return (
    <Button
      variant="default"
      active={isOpen}
      onClick={onClick}
      title={isOpen ? 'Hide page thumbnails' : 'Show page thumbnails'}
      style={{
        position: 'absolute',
        top: 10,
        left: 10,
        padding: '6px 10px',
        fontSize: '11px',
        gap: '6px',
        zIndex: 50,
      }}
    >
      <span style={{ fontSize: '12px' }}>{isOpen ? '◀' : '▶'}</span>
      <span>Pages ({currentPage}/{pageCount})</span>
    </Button>
  );
};

export default PDFPageSidebar;
